/**
 * Session storage for the SEEN API client.
 *
 * Access token lives in memory only (never persisted), the long-lived refresh
 * token is kept in `localStorage` so a reload can silently restore the session.
 * Refreshes are single-flight: concurrent 401s share one `/auth/refresh` call.
 */

const REFRESH_KEY = "seen.refresh";
const CART_TOKEN_KEY = "seen.cart-token";

export interface SessionTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
}

let accessToken: string | null = null;
let refreshInFlight: Promise<string | null> | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

export function onSessionChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getRefreshToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(REFRESH_KEY);
}

export function setSession(tokens: SessionTokens | null): void {
  accessToken = tokens?.accessToken ?? null;
  if (typeof window !== "undefined") {
    if (tokens?.refreshToken) window.localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
    else window.localStorage.removeItem(REFRESH_KEY);
  }
  notify();
}

export function clearSession(): void {
  setSession(null);
}

export function hasSession(): boolean {
  return Boolean(accessToken || getRefreshToken());
}

/** Anonymous cart identity so guests keep their basket across reloads. */
export function getCartToken(): string {
  if (typeof window === "undefined") return "";
  let token = window.localStorage.getItem(CART_TOKEN_KEY);
  if (!token) {
    token =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `c-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    window.localStorage.setItem(CART_TOKEN_KEY, token);
  }
  return token;
}

/** Reset guest cart token so the previous order basket is fully detached. */
export function resetCartToken(): string {
  if (typeof window === "undefined") return "";
  const token =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `c-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  window.localStorage.setItem(CART_TOKEN_KEY, token);
  return token;
}

/**
 * Rotates the token pair. Uses a raw `fetch` (not `apiFetch`) to avoid the
 * retry-on-401 recursion. Returns the new access token, or null when the
 * refresh token is missing/expired — in which case the session is cleared.
 */
export function refreshSession(baseUrl: string): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(null);

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${baseUrl}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        clearSession();
        return null;
      }
      const payload = (await response.json()) as { ok?: boolean; data?: SessionTokens };
      if (!payload?.data?.accessToken) {
        clearSession();
        return null;
      }
      setSession(payload.data);
      return payload.data.accessToken;
    } catch {
      clearSession();
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}
